import * as faceapi from 'face-api.js';
import { extractExif } from './exifUtils';

// ---- Types ----------------------------------------------------------------

export interface PhotoAnalysis {
  photoId: string;
  faceCount: number;
  faceBoxes: { x: number; y: number; width: number; height: number }[];
  exifDate: string | null;
  gpsLat: number | null;
  gpsLng: number | null;
  width: number;
  height: number;
  aspectRatio: 'landscape' | 'portrait' | 'square';
}

// ---- Model loading (singleton) --------------------------------------------

let modelsLoaded = false;

async function loadModels(): Promise<void> {
  if (modelsLoaded) return;
  await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
  // faceLandmark68Net intentionally NOT loaded — we only read the detection
  // box (face_count + crop centering), never the 68 landmarks. Loading it wasted
  // memory + a model fetch for nothing. (lazyPhotoAnalysis already skips it.)
  modelsLoaded = true;
}

/** Reset so models reload on next use. Paired with lazyPhotoAnalysis.releaseModels
 *  (which disposes the shared ssdMobilenetv1 tensors) when freeing memory before
 *  the iOS photo picker opens. */
export function releaseModels(): void {
  modelsLoaded = false;
}

// ---- Helpers --------------------------------------------------------------

function isHeic(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for analysis'));
    img.src = url;
  });
}

function classifyAspectRatio(
  width: number,
  height: number,
): 'landscape' | 'portrait' | 'square' {
  const ratio = width / height;
  if (ratio > 1.05) return 'landscape';
  if (ratio < 0.95) return 'portrait';
  return 'square';
}

// 64-bit dHash → 16-char hex. Mirror of lazyPhotoAnalysis.computeDHash.
// Two photos with Hamming distance < 12 are visually near-identical → burst.
function computeDHash(img: HTMLImageElement): string {
  const W = 9, H = 8;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return '';
  ctx.drawImage(img, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data;
  const gray = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) {
    gray[i] = (data[i * 4] * 299 + data[i * 4 + 1] * 587 + data[i * 4 + 2] * 114) / 1000;
  }
  let bits = '';
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W - 1; x++) {
      bits += gray[y * W + x] > gray[y * W + x + 1] ? '1' : '0';
    }
  }
  let hex = '';
  for (let i = 0; i < 64; i += 4) {
    hex += parseInt(bits.slice(i, i + 4), 2).toString(16);
  }
  return hex;
}

// ---- Metadata POST helper -------------------------------------------------

async function postMetadata(
  apiBase: string,
  photoId: string,
  metadata: Record<string, unknown>,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const res = await fetch(`${apiBase}/photos/${photoId}/metadata`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(metadata),
    });
    if (!res.ok) {
      console.error(
        `[photoAnalyzer] Failed to POST metadata for ${photoId}: ${res.status} ${res.statusText}`,
      );
    }
  } catch (err) {
    console.error(`[photoAnalyzer] Network error posting metadata for ${photoId}:`, err);
  }
}

// ---- Main pipeline --------------------------------------------------------

/**
 * Full analysis pipeline for a single photo.
 *
 * For browser-renderable formats (JPG, PNG, WebP):
 *   EXIF → load image → face detection → dimensions → POST metadata
 *
 * For HEIC/HEIF (not renderable in most browsers):
 *   EXIF only → POST metadata with no face data / zero dimensions
 *   The photo is still uploaded and usable — face detection is skipped.
 */
export async function analyzePhoto(
  file: File,
  photoId: string,
  apiBase: string,
  authHeaders: Record<string, string>,
): Promise<PhotoAnalysis> {
  const fileSizeKb = Math.round(file.size / 1024);

  // --- EXIF extraction works for all formats (reads raw bytes) ─────────────
  const exif = await extractExif(file);

  // --- HEIC: browsers can't render in <img>, skip face detection ──────────
  if (isHeic(file)) {
    const metadata = {
      face_count: 0,
      face_data: [],
      exif_date: exif.date,
      exif_gps_lat: exif.lat,
      exif_gps_lng: exif.lng,
      is_blurry: false,
      width: 0,
      height: 0,
      file_size_kb: fileSizeKb,
    };

    await postMetadata(apiBase, photoId, metadata, authHeaders);

    return {
      photoId,
      faceCount: 0,
      faceBoxes: [],
      exifDate: exif.date,
      gpsLat: exif.lat,
      gpsLng: exif.lng,
      width: 0,
      height: 0,
      aspectRatio: 'landscape', // default for HEIC (typically phone photos)
    };
  }

  // --- Standard formats: full pipeline ────────────────────────────────────
  const objectUrl = URL.createObjectURL(file);

  try {
    await loadModels();

    const img = await loadImage(objectUrl);

    const detections = await faceapi
      .detectAllFaces(img, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }));

    const faceBoxes = detections.map((d) => {
      const box = d.box;
      return {
        x: Math.round(box.x),
        y: Math.round(box.y),
        width: Math.round(box.width),
        height: Math.round(box.height),
      };
    });

    const width = img.naturalWidth;
    const height = img.naturalHeight;
    const aspectRatio = classifyAspectRatio(width, height);
    const phash = computeDHash(img);

    const metadata = {
      face_count: faceBoxes.length,
      face_data: faceBoxes,
      exif_date: exif.date,
      exif_gps_lat: exif.lat,
      exif_gps_lng: exif.lng,
      is_blurry: false,
      width,
      height,
      file_size_kb: fileSizeKb,
      phash,
    };

    await postMetadata(apiBase, photoId, metadata, authHeaders);

    return {
      photoId,
      faceCount: faceBoxes.length,
      faceBoxes,
      exifDate: exif.date,
      gpsLat: exif.lat,
      gpsLng: exif.lng,
      width,
      height,
      aspectRatio,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
